import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../api/client';

interface User {
    id: string;
    email: string;
    createdAt: string;
}

interface UserSettings {
    userId: string;
    defaultStopLossPct: number;
}

interface AuthContextType {
    user: User | null;
    settings: UserSettings | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
    signup: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
    logout: () => Promise<void>;
    updateSettings: (defaultStopLossPct: number) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [settings, setSettings] = useState<UserSettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        checkAuth();
    }, []);

    async function checkAuth() {
        try {
            const response = await api.getMe();
            if (response.success) {
                setUser(response.data.user);
                setSettings(response.data.settings);
            }
        } catch {
            // Not authenticated
        } finally {
            setIsLoading(false);
        }
    }

    async function login(email: string, password: string) {
        const response = await api.login(email, password);
        if (response.success) {
            setUser(response.data.user);
            setSettings(response.data.settings);
            return { success: true };
        }
        return { success: false, error: response.error };
    }

    async function signup(email: string, password: string) {
        const response = await api.signup(email, password);
        if (response.success) {
            setUser(response.data.user);
            setSettings(response.data.settings);
            return { success: true };
        }
        return { success: false, error: response.error };
    }

    async function logout() {
        await api.logout();
        setUser(null);
        setSettings(null);
    }

    async function updateSettings(defaultStopLossPct: number) {
        const response = await api.updateSettings(defaultStopLossPct);
        if (response.success && settings) {
            setSettings({ ...settings, defaultStopLossPct });
        }
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                settings,
                isLoading,
                isAuthenticated: !!user,
                login,
                signup,
                logout,
                updateSettings,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
